/**
 * hooks/useFirestoreRevenue.ts
 *
 * Returns canonical revenue numbers for the current doctor.
 *
 * IMPORTANT: Despite the legacy name, numbers come from the backend
 * (/api/revenue/doctor/:id/today and /history), NEVER from Firestore. Firestore
 * is only used as a real-time PING — when clinics/{clinicId}/dailyRevenue/{date}
 * is touched, we know "something money-related changed" and re-fetch the BE.
 * This eliminates the dual-write drift bug between Firestore and MongoDB.
 *
 * The old behavior (reading collected from Firestore, writing backfill from
 * waiting_list patients) is gone. BE is the single source of truth.
 *
 * Returns:
 *   collected — today's canonical net revenue (number | null)
 *   history   — last 60 Egypt days, newest first
 *   isLoading — true until the first BE fetch resolves
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import axios from 'axios';
import { db } from '../firebaseConfig';
import API from '../config/api';

export interface DayRevenue {
    date: string;       // "YYYY-M-D" (Egypt local day)
    collected: number;  // canonical net revenue for that day from BE
}

export interface UseFirestoreRevenueResult {
    collected: number | null;
    history: DayRevenue[];
    isLoading: boolean;
}

// Normalize a zero-padded "YYYY-MM-DD" from BE to the "YYYY-M-D" format the
// rest of the dashboard uses when matching Firestore docs.
function denormalizeDate(zero: string): string {
    const [y, m, d] = zero.split('-').map(Number);
    return `${y}-${m}-${d}`;
}

export function useFirestoreRevenue(): UseFirestoreRevenueResult {
    const [collected, setCollected] = useState<number | null>(null);
    const [history, setHistory] = useState<DayRevenue[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const inFlightRef = useRef(false);

    const fetchFromBE = useCallback(async () => {
        const doctorId = localStorage.getItem('doctorId');
        if (!doctorId) {
            setCollected(null);
            setHistory([]);
            setIsLoading(false);
            return;
        }
        if (inFlightRef.current) return; // dedupe rapid pings
        inFlightRef.current = true;
        try {
            const [todayRes, historyRes] = await Promise.all([
                axios.get(`${API.BASE_URL}${API.ENDPOINTS.REVENUE_TODAY(doctorId)}`),
                axios.get(`${API.BASE_URL}${API.ENDPOINTS.REVENUE_HISTORY(doctorId)}`, {
                    params: { days: 60 },
                }),
            ]);
            if (todayRes.data?.success) {
                setCollected(Number(todayRes.data.data?.totalRevenue) || 0);
            }
            if (historyRes.data?.success) {
                const rows: DayRevenue[] = (historyRes.data.data || []).map((r: any) => ({
                    date: denormalizeDate(r.date),
                    collected: Number(r.totalRevenue) || 0,
                }));
                setHistory(rows);
            }
        } catch (err) {
            // Silent — keep prior values rather than zeroing the indicator.
            console.warn('[useFirestoreRevenue] BE fetch failed:', err);
        } finally {
            setIsLoading(false);
            inFlightRef.current = false;
        }
    }, []);

    useEffect(() => {
        const clinicId = localStorage.getItem('clinicId');
        const doctorId = localStorage.getItem('doctorId');
        if (!doctorId) {
            setIsLoading(false);
            return;
        }

        // Initial load
        fetchFromBE();

        // Firestore ping listener: any change in this clinic's dailyRevenue
        // (created by BE after a money operation lands) triggers a BE refetch.
        let unsub: (() => void) | null = null;
        if (clinicId) {
            try {
                unsub = onSnapshot(
                    collection(db, 'clinics', clinicId, 'dailyRevenue'),
                    (snap) => {
                        // Skip the initial replay (it fires on mount with all docs)
                        if (snap.metadata.hasPendingWrites) return;
                        fetchFromBE();
                    },
                    (err) => console.warn('[useFirestoreRevenue] ping snapshot error:', err),
                );
            } catch (err) {
                console.warn('[useFirestoreRevenue] ping setup failed:', err);
            }
        }

        // Fallback polling — every 60s, in case Firestore ping is offline.
        const intervalId = setInterval(fetchFromBE, 60_000);

        // Also re-fetch when the BillingModal explicitly dispatches a revenueChanged event.
        const onChange = () => fetchFromBE();
        window.addEventListener('revenueChanged', onChange);

        return () => {
            if (unsub) unsub();
            clearInterval(intervalId);
            window.removeEventListener('revenueChanged', onChange);
        };
    }, [fetchFromBE]);

    return { collected, history, isLoading };
}
